import { action, computed, makeObservable, observable, reaction } from 'mobx';
import type { RootStore } from '.';
import { db } from './data';

export class UiTimeSavedStore {
  private rootStore: RootStore;
  private connectionCount = 0;
  private persistedTimeSaved = 0;
  private readonly TIME_PER_CONNECTION = 15; // seconds
  private saveTimeoutId: NodeJS.Timeout | null = null;
  private isInitialized = false;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;

    makeObservable<UiTimeSavedStore, 'connectionCount' | 'persistedTimeSaved' | 'isInitialized'>(
      this,
      {
        connectionCount: observable,
        persistedTimeSaved: observable,
        isInitialized: observable,
        timeSavedInSeconds: computed,
        incrementConnections: action.bound,
        decrementConnections: action.bound,
        updateConnectionCount: action.bound,
        setConnectionCountDirectly: action.bound,
        setPersistedTimeSaved: action.bound,
        setInitialized: action.bound,
      },
    );

    // Initialize connection count and load persisted time by watching for session changes
    reaction(
      () => this.rootStore.sessionsStore.currentSession,
      async (currentSession, prevSession) => {
        // Save time for previous session before switching
        if (prevSession) {
          await this.saveTimeSaved(prevSession.id);
        }

        // Reset counts when switching sessions
        this.setConnectionCountDirectly(0);
        this.setInitialized(false);

        if (currentSession) {
          // Load persisted time for new session
          // Skip loading for guest users (they can't read session data directly)
          const { user } = this.rootStore.authStore;
          if (user?.isGuest) {
            this.setPersistedTimeSaved(0);
            this.setInitialized(true);
            return;
          }

          try {
            const doc = await db.sessions.doc(currentSession.id).get();
            const data = doc.data();
            this.setPersistedTimeSaved(data?.timeSavedInSeconds || 0);

            // Wait for cards and modules to be loaded before calculating connections
            await this.waitForDataToLoad();

            // Count all connections in all cards
            let totalConnections = 0;
            Object.values(this.rootStore.cardsStore.cards).forEach((card) => {
              Object.values(currentSession.data.modules).forEach((module) => {
                const connections = card.getModuleConnections(module.id);
                totalConnections += connections.length;
              });
            });
            this.setConnectionCountDirectly(totalConnections);
            this.setInitialized(true);
          } catch (error) {
            console.error('Error loading time saved data:', error);
            this.setPersistedTimeSaved(0);
            this.setInitialized(true);
          }
        } else {
          this.setPersistedTimeSaved(0);
          this.setInitialized(true);
        }
      },
      { fireImmediately: true },
    );

    // Save time periodically and before window unload
    this.setupAutoSave();
  }

  private async waitForDataToLoad(): Promise<void> {
    // Wait for cards to be loaded
    const maxAttempts = 50; // 5 seconds max wait

    const checkDataLoaded = async (attempts: number): Promise<void> => {
      const { currentSession } = this.rootStore.sessionsStore;
      if (!currentSession || attempts >= maxAttempts) return;

      // Check if cards are loaded
      const cardIds = currentSession.data.cardIds || [];
      const loadedCards = Object.keys(this.rootStore.cardsStore.cards);

      if (cardIds.length === 0 || loadedCards.length >= cardIds.length) {
        return;
      }

      await new Promise((resolve) => setTimeout(resolve, 100));
      return checkDataLoaded(attempts + 1);
    };

    await checkDataLoaded(0);
  }

  private setupAutoSave() {
    // Save every 30 seconds (more frequent for better persistence)
    const AUTOSAVE_INTERVAL = 30000;

    const autoSave = async () => {
      const { currentSession } = this.rootStore.sessionsStore;
      if (currentSession && this.isInitialized) {
        await this.saveTimeSaved(currentSession.id);
      }
    };

    // Set up periodic save
    setInterval(autoSave, AUTOSAVE_INTERVAL);

    // Save before window unload with synchronous approach
    window.addEventListener('beforeunload', () => {
      const { currentSession } = this.rootStore.sessionsStore;
      if (currentSession && this.isInitialized) {
        // Use synchronous approach for beforeunload
        const totalTime = this.timeSavedInSeconds;
        try {
          // Use sendBeacon for reliable data transmission during page unload
          // If sendBeacon is available, use it
          if (navigator.sendBeacon && typeof navigator.sendBeacon === 'function') {
            // Note: This would require a server endpoint to handle the beacon
            // For now, we'll use the existing async method but with a shorter timeout
            UiTimeSavedStore.saveTimeSavedSync(currentSession.id, totalTime);
          } else {
            UiTimeSavedStore.saveTimeSavedSync(currentSession.id, totalTime);
          }
        } catch (error) {
          console.error('Error saving time saved on unload:', error);
        }
      }
    });

    // Also save on page visibility change (when user switches tabs)
    document.addEventListener('visibilitychange', async () => {
      if (document.visibilityState === 'hidden') {
        const { currentSession } = this.rootStore.sessionsStore;
        if (currentSession && this.isInitialized) {
          await this.saveTimeSaved(currentSession.id);
        }
      }
    });
  }

  get timeSavedInSeconds(): number {
    return this.persistedTimeSaved + this.connectionCount * this.TIME_PER_CONNECTION;
  }

  private async saveTimeSaved(sessionId: string) {
    try {
      // Skip saving for guest users or users without edit permissions
      const { user } = this.rootStore.authStore;
      const { sessionsStore } = this.rootStore;

      if (user?.isGuest) {
        // Guest users can't write to session documents
        return;
      }

      // Check permissions for the specific session being saved (not currentSession)
      // This is important when navigating away from a session - currentSession might already be different
      const sessionToSave =
        sessionsStore.sessions[sessionId] || sessionsStore.sharedSessions[sessionId];
      if (sessionToSave && !sessionToSave.canEditSession) {
        // User doesn't have edit permissions for this session, skip saving
        return;
      }

      const totalTime = this.timeSavedInSeconds;
      await db.sessions.doc(sessionId).set(
        {
          timeSavedInSeconds: totalTime,
        },
        { merge: true },
      );
      // Update persisted time after successful save
      this.setPersistedTimeSaved(totalTime);
      // Reset connection count directly without triggering another save
      this.setConnectionCountDirectly(0);
    } catch (error) {
      console.error('Error saving time saved:', error);
    }
  }

  private static saveTimeSavedSync(sessionId: string, totalTime: number) {
    // Synchronous version for beforeunload
    try {
      // Use a synchronous approach - this is a fallback
      // The async version should handle most cases
      db.sessions
        .doc(sessionId)
        .set(
          {
            timeSavedInSeconds: totalTime,
          },
          { merge: true },
        )
        .catch((error) => {
          console.error('Error in sync save:', error);
        });
    } catch (error) {
      console.error('Error in sync save time saved:', error);
    }
  }

  setPersistedTimeSaved(seconds: number) {
    this.persistedTimeSaved = seconds;
  }

  setInitialized(initialized: boolean) {
    this.isInitialized = initialized;
  }

  incrementConnections(count: number = 1) {
    this.connectionCount += count;
    // Schedule a save after increment
    this.debouncedSave();
  }

  decrementConnections(count: number = 1) {
    this.connectionCount = Math.max(0, this.connectionCount - count);
    // Schedule a save after decrement
    this.debouncedSave();
  }

  updateConnectionCount(newCount: number) {
    this.connectionCount = Math.max(0, newCount);
    // Schedule a save after update
    this.debouncedSave();
  }

  setConnectionCountDirectly(newCount: number) {
    // Set connection count without triggering a save (for internal use)
    this.connectionCount = Math.max(0, newCount);
  }

  private debouncedSave() {
    // Clear any pending save
    if (this.saveTimeoutId) {
      clearTimeout(this.saveTimeoutId);
    }

    // Schedule a new save in 1 second (reduced from 2 seconds for better responsiveness)
    this.saveTimeoutId = setTimeout(async () => {
      const { currentSession } = this.rootStore.sessionsStore;
      if (currentSession && this.isInitialized) {
        await this.saveTimeSaved(currentSession.id);
      }
    }, 1000);
  }
}
